/**
 * Lectura, indices y auditoria editorial.
 *
 * Todo lo que el admin muestra como "analisis" sale de aca, y usa exactamente
 * los mismos datos que usa el renderer, asi que el checklist no puede
 * contradecir lo que realmente se renderiza.
 *
 * Importante: nada de esto predice posiciones en buscadores. Es una ayuda
 * editorial para decidir si el articulo esta listo, no un score.
 */

import { docToPlainText } from "./document";
import { renderRichContent } from "./render";
import { collapseWhitespace, isExternalUrl, isInternalUrl } from "./text";
import type { FaqEntry, ImageAudit, ReadingStats, RichDoc, RichNode, TocEntry } from "./types";

const WORDS_PER_MINUTE = 200;

export function readingStats(doc: RichDoc): ReadingStats {
  const text = docToPlainText(doc);
  const words = text ? text.split(/\s+/).filter(Boolean).length : 0;
  return { words, minutes: Math.max(1, Math.ceil(words / WORDS_PER_MINUTE)) };
}

export function imageAudit(doc: RichDoc): ImageAudit {
  let total = 0;
  let withoutAlt = 0;

  const walk = (node: RichNode) => {
    if (node.type === "image") {
      total += 1;
      const alt = String(node.attrs?.alt ?? "").trim();
      if (!alt) withoutAlt += 1;
    }
    node.content?.forEach(walk);
  };

  doc.content.forEach(walk);
  return { total, withoutAlt };
}

export interface LinkAudit {
  internal: number;
  external: number;
}

export function linkAudit(doc: RichDoc): LinkAudit {
  let internal = 0;
  let external = 0;

  const walk = (node: RichNode) => {
    if (node.type === "text" && node.marks) {
      for (const mark of node.marks) {
        if (mark.type !== "link") continue;
        const href = String(mark.attrs?.href ?? "");
        if (isExternalUrl(href)) external += 1;
        else if (isInternalUrl(href)) internal += 1;
      }
    }
    node.content?.forEach(walk);
  };

  doc.content.forEach(walk);
  return { internal, external };
}

/** Arbol de estructura del articulo, para revision en el admin. */
export interface OutlineNode {
  label: string;
  level: number | null;
  kind: "heading" | "paragraph" | "list" | "image" | "quote" | "table" | "faq" | "rule" | "code";
  preview: string;
}

export function buildOutline(doc: RichDoc): OutlineNode[] {
  const outline: OutlineNode[] = [];

  const label = (node: RichNode) =>
    collapseWhitespace((node.content ?? []).map((child) => child.text ?? "").join(" "));

  for (const node of doc.content) {
    switch (node.type) {
      case "heading":
        outline.push({
          label: label(node),
          level: node.attrs?.level === 3 ? 3 : 2,
          kind: "heading",
          preview: label(node),
        });
        break;
      case "bulletList":
      case "orderedList": {
        const count = node.content?.length ?? 0;
        outline.push({
          label: node.type === "orderedList" ? `Lista numerada (${count})` : `Lista con vinetas (${count})`,
          level: null,
          kind: "list",
          preview: (node.content ?? [])
            .map((item) => label(item))
            .join(" · ")
            .slice(0, 160),
        });
        break;
      }
      case "image":
        outline.push({
          label: String(node.attrs?.caption ?? node.attrs?.alt ?? "Imagen"),
          level: null,
          kind: "image",
          preview: String(node.attrs?.src ?? ""),
        });
        break;
      case "blockquote":
        outline.push({ label: "Cita", level: null, kind: "quote", preview: label(node).slice(0, 160) });
        break;
      case "table":
        outline.push({
          label: `Tabla (${node.content?.length ?? 0} filas)`,
          level: null,
          kind: "table",
          preview: "",
        });
        break;
      case "faqList":
        outline.push({
          label: `FAQ (${node.content?.length ?? 0} preguntas)`,
          level: null,
          kind: "faq",
          preview: (node.content ?? []).map((item) => String(item.attrs?.question ?? "")).join(" · "),
        });
        break;
      case "horizontalRule":
        outline.push({ label: "Separador", level: null, kind: "rule", preview: "" });
        break;
      case "codeBlock":
        outline.push({ label: "Codigo", level: null, kind: "code", preview: label(node).slice(0, 160) });
        break;
      default: {
        const preview = label(node);
        outline.push({
          label: preview ? `${preview.slice(0, 80)}${preview.length > 80 ? "…" : ""}` : "Parrafo vacio",
          level: null,
          kind: "paragraph",
          preview,
        });
      }
    }
  }

  return outline;
}

export type CheckStatus = "ok" | "warn" | "error";

export interface SeoCheck {
  id: string;
  label: string;
  status: CheckStatus;
  detail: string;
}

export interface SeoAnalysisInput {
  doc: RichDoc;
  title: string;
  excerpt: string;
  slug: string;
  seoTitle: string;
  seoDescription: string;
  coverImageUrl: string;
  coverImageAlt: string;
  focusKeyword: string;
  secondaryKeywords: string[];
  toc: TocEntry[];
}

export interface SeoAnalysis {
  checks: SeoCheck[];
  keyword: KeywordAnalysis | null;
  stats: ReadingStats;
  images: ImageAudit;
  links: LinkAudit;
  faq: FaqEntry[];
}

export interface KeywordAnalysis {
  keyword: string;
  inTitle: boolean;
  inIntroduction: boolean;
  inHeading: boolean;
  inContent: boolean;
  inSlug: boolean;
  inSeoTitle: boolean;
  inSeoDescription: boolean;
  densityPercent: number;
}

const META_TITLE_MAX = 60;
const META_DESCRIPTION_MAX = 160;
const MIN_WORDS = 300;

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function countOccurrences(haystack: string, needle: string): number {
  if (!needle) return 0;
  const hay = normalize(haystack);
  const pin = normalize(needle);
  let count = 0;
  let index = hay.indexOf(pin);
  while (index !== -1) {
    count += 1;
    index = hay.indexOf(pin, index + pin.length);
  }
  return count;
}

export function analyzeKeyword(input: Omit<SeoAnalysisInput, "toc">): KeywordAnalysis | null {
  const keyword = input.focusKeyword.trim();
  if (!keyword) return null;

  const plain = docToPlainText(input.doc);
  const intro = plain
    .split(/\n{2,}/)
    .slice(0, 2)
    .join(" ");
  const headings = collectHeadingText(input.doc);
  const occurrences = countOccurrences(plain, keyword);
  const words = plain ? plain.split(/\s+/).filter(Boolean).length : 0;

  return {
    keyword,
    inTitle: normalize(input.title).includes(normalize(keyword)),
    inIntroduction: normalize(intro).includes(normalize(keyword)),
    inHeading: headings.some((heading) => normalize(heading).includes(normalize(keyword))),
    inContent: occurrences > 0,
    inSlug: normalize(input.slug).includes(normalize(keyword)),
    inSeoTitle: normalize(input.seoTitle).includes(normalize(keyword)),
    inSeoDescription: normalize(input.seoDescription).includes(normalize(keyword)),
    // Solo informativo: se reporta para que el editor juzgue, no como objetivo.
    densityPercent:
      words > 0 ? Number(((occurrences * keyword.split(/\s+/).length * 100) / words).toFixed(2)) : 0,
  };
}

function collectHeadingText(doc: RichDoc): string[] {
  const result: string[] = [];
  const walk = (nodes: RichNode[]) => {
    for (const node of nodes) {
      if (node.type === "heading")
        result.push((node.content ?? []).map((child) => child.text ?? "").join(""));
      if (node.content) walk(node.content);
    }
  };
  walk(doc.content);
  return result;
}

/**
 * Checklist editorial. No es un score ni una promesa de posicionamiento:
 * cada item describe un hecho verificable sobre el contenido.
 */
export function analyzeSeo(input: SeoAnalysisInput): SeoAnalysis {
  const checks: SeoCheck[] = [];
  const stats = readingStats(input.doc);
  const images = imageAudit(input.doc);
  const links = linkAudit(input.doc);
  const faq = renderRichContent(input.doc).faq;

  // El H1 siempre existe: lo genera el titulo de la publicacion.
  checks.push({
    id: "h1",
    label: "H1 encontrado",
    status: input.title.trim() ? "ok" : "error",
    detail: input.title.trim()
      ? `El titulo de la publicacion actua como H1 unico: "${input.title.trim()}"`
      : "Falta el titulo, que es el H1 de la pagina",
  });

  const h2 = input.toc.filter((entry) => entry.level === 2).length;
  checks.push({
    id: "h2",
    label: "H2 encontrados",
    status: h2 >= 2 ? "ok" : h2 === 1 ? "warn" : "error",
    detail:
      h2 > 0
        ? `${h2} subtitulo(s) de segundo nivel`
        : "Ningun H2: el articulo no se puede recorrer por indice",
  });

  const h3 = input.toc.filter((entry) => entry.level === 3).length;
  checks.push({
    id: "h3",
    label: "H3 encontrados",
    status: h3 > 0 ? "ok" : "warn",
    detail: h3 > 0 ? `${h3} subseccion(es) de tercer nivel` : "Sin H3. Opcional, util en articulos largos",
  });

  checks.push({
    id: "cover",
    label: "Imagen principal",
    status: input.coverImageUrl ? "ok" : "warn",
    detail: input.coverImageUrl ? "Hay imagen principal configurada" : "Sin imagen principal",
  });

  checks.push({
    id: "cover-alt",
    label: "ALT en imagen principal",
    status: !input.coverImageUrl ? "warn" : input.coverImageAlt.trim() ? "ok" : "warn",
    detail: !input.coverImageUrl
      ? "No aplica: no hay imagen principal"
      : input.coverImageAlt.trim()
        ? "La imagen principal describe su contenido"
        : "La imagen principal no tiene ALT",
  });

  checks.push({
    id: "images-alt",
    label: "ALT en imagenes del contenido",
    status: images.total === 0 ? "warn" : images.withoutAlt === 0 ? "ok" : "warn",
    detail:
      images.total === 0
        ? "El articulo no tiene imagenes de contenido"
        : images.withoutAlt === 0
          ? `${images.total} imagen(es) con ALT`
          : `${images.withoutAlt} de ${images.total} imagen(es) sin ALT`,
  });

  checks.push({
    id: "internal-links",
    label: "Enlaces internos",
    status: links.internal > 0 ? "ok" : "warn",
    detail:
      links.internal > 0
        ? `${links.internal} enlace(s) a paginas de Vimos`
        : "Sin enlaces a otras paginas del sitio",
  });

  checks.push({
    id: "external-links",
    label: "Enlaces externos",
    status: links.external > 0 ? "ok" : "warn",
    detail: links.external > 0 ? `${links.external} enlace(s) externos` : "Sin fuentes externas",
  });

  checks.push({
    id: "meta-title",
    label: "Meta title",
    status: !input.seoTitle.trim() ? "warn" : input.seoTitle.length > META_TITLE_MAX ? "warn" : "ok",
    detail: !input.seoTitle.trim()
      ? "Sin titulo SEO: se usara el titulo de la publicacion"
      : input.seoTitle.length > META_TITLE_MAX
        ? `${input.seoTitle.length} caracteres (recomendado hasta ${META_TITLE_MAX})`
        : `${input.seoTitle.length} caracteres`,
  });

  checks.push({
    id: "meta-description",
    label: "Meta description",
    status: !input.seoDescription.trim()
      ? "warn"
      : input.seoDescription.length > META_DESCRIPTION_MAX
        ? "warn"
        : "ok",
    detail: !input.seoDescription.trim()
      ? "Sin descripcion SEO: se usara el extracto"
      : input.seoDescription.length > META_DESCRIPTION_MAX
        ? `${input.seoDescription.length} caracteres (recomendado hasta ${META_DESCRIPTION_MAX})`
        : `${input.seoDescription.length} caracteres`,
  });

  checks.push({
    id: "slug",
    label: "URL amigable",
    status: /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(input.slug) ? "ok" : input.slug ? "warn" : "error",
    detail: input.slug ? `/blog/${input.slug}` : "Falta el slug",
  });

  checks.push({
    id: "excerpt",
    label: "Extracto visible",
    status: input.excerpt.trim() ? "ok" : "error",
    detail: input.excerpt.trim()
      ? `${input.excerpt.trim().length} caracteres`
      : "Falta el extracto para listados",
  });

  checks.push({
    id: "length",
    label: "Contenido suficiente",
    status: stats.words >= MIN_WORDS ? "ok" : stats.words >= 150 ? "warn" : "error",
    detail: `${stats.words} palabras (~${stats.minutes} min de lectura)`,
  });

  checks.push({
    id: "faq",
    label: "Bloque FAQ visible",
    status: faq.length > 0 ? "ok" : "warn",
    detail:
      faq.length > 0
        ? `${faq.length} pregunta(s); se generara FAQPage porque son visibles en la pagina`
        : "Sin bloque FAQ. Solo se agrega FAQPage si existen preguntas visibles",
  });

  const keyword = analyzeKeyword(input);

  if (keyword) {
    checks.push({
      id: "keyword",
      label: "Palabra clave principal",
      status: keyword.inTitle && keyword.inContent ? "ok" : "warn",
      detail:
        keyword.inTitle && keyword.inContent
          ? `"${keyword.keyword}" aparece en el titulo y en el contenido`
          : `Presencia de "${keyword.keyword}": ${[
              keyword.inTitle ? "titulo" : null,
              keyword.inIntroduction ? "introduccion" : null,
              keyword.inHeading ? "H2/H3" : null,
              keyword.inContent ? "contenido" : null,
              keyword.inSlug ? "slug" : null,
              keyword.inSeoTitle ? "meta title" : null,
              keyword.inSeoDescription ? "meta description" : null,
            ]
              .filter(Boolean)
              .join(", ")}${keyword.inTitle || keyword.inContent ? "" : " (sin presencia)"}`,
    });

    // Aviso, no penalizacion: el objetivo es lenguaje natural.
    if (keyword.densityPercent > 4) {
      checks.push({
        id: "keyword-density",
        label: "Densidad de palabra clave",
        status: "warn",
        detail: `${keyword.densityPercent}% de densidad. Revisar que suene natural en lugar de repetir`,
      });
    }
  } else {
    checks.push({
      id: "keyword",
      label: "Palabra clave principal",
      status: "warn",
      detail: "Sin palabra clave principal definida para este articulo",
    });
  }

  return { checks, keyword, stats, images, links, faq };
}

export const SEO_LIMIT = {
  metaTitleMax: META_TITLE_MAX,
  metaDescriptionMax: META_DESCRIPTION_MAX,
  minWords: MIN_WORDS,
};
